import { z } from 'zod';
import { uuidSchema } from '../schema/rows.js';
import { blockKindSchema, stageCategorySchema, stageKindScopeSchema, } from '../template/manifest.js';
import { highlightEligibility, withdrawIneligible } from './stage.js';
import { DomainError, isActionableKind } from './types.js';
/**
 * Converting a block into another kind (PRO-05, docs/03 §3.1, design D1–D3 of
 * f1-type-conversion). Title, body, children, tags, fields, dates and `props` stay; the stage and
 * the status follow the new kind (INV-03, INV-04), highlights that stop being eligible are
 * withdrawn (INV-07), and the undo restores every column that changed. A file needs an attached
 * asset (F3), so it is not a target.
 */
const stageInput = z.object({
    id: uuidSchema,
    key: z.string(),
    category: stageCategorySchema,
    kind_scope: stageKindScopeSchema,
    is_capture_default: z.union([z.boolean(), z.literal(0), z.literal(1)]).optional(),
});
const convertKindInput = z.object({
    block: z.object({
        id: uuidSchema,
        kind: blockKindSchema,
        parent_id: uuidSchema.nullable(),
        stage_id: uuidSchema.nullable(),
        stage_changed_at: z.string().nullable().default(null),
        status: z.enum(['open', 'done', 'cancelled']).nullable(),
        completed_at: z.string().nullable(),
        /** As stored: an object, its JSON text or nothing. */
        props: z.unknown().optional(),
    }),
    target: blockKindSchema,
    /** Stages of the block's workspace. */
    stages: z.array(stageInput),
    /** Active highlights of the block, with what they require. */
    highlights: z.array(highlightEligibility).default([]),
    /** Address of the link, when the target is `link`. */
    url: z.string().optional(),
    now: z.string().optional(),
});
/** An `http(s)` address with a host and no spaces: never `javascript:` and friends. */
const LINK_URL = /^https?:\/\/[^\s/?#@]+(?:[/?#]\S*)?$/i;
/** Only plain web addresses for a link (the domain has no DOM or Node `URL`). */
export function isValidLinkUrl(url) {
    return LINK_URL.test(url);
}
/** First web address in the title or the note, proposed when converting into a link. */
export function suggestLinkUrl(title, body) {
    for (const text of [title, body ?? '']) {
        const match = /https?:\/\/[^\s<>()"'`]+/i.exec(text);
        if (match) {
            const url = match[0].replace(/[.,;:!?]+$/, '');
            if (isValidLinkUrl(url))
                return url;
        }
    }
    return null;
}
function parseProps(props) {
    if (props === null || props === undefined || props === '')
        return {};
    if (typeof props === 'string') {
        try {
            const parsed = JSON.parse(props);
            return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
                ? parsed
                : {};
        }
        catch {
            return {};
        }
    }
    return typeof props === 'object' && !Array.isArray(props)
        ? props
        : {};
}
/** Where the block goes for its new kind (design D1). */
function targetStage(target, block, stages) {
    const current = stages.find((stage) => stage.id === block.stage_id);
    const need = (stage, what) => {
        if (!stage)
            throw new DomainError('invalid_input', `El espacio no tiene ${what}.`, 'INV-04');
        return stage;
    };
    if (target === 'project') {
        return need(stages.find((s) => s.kind_scope === 'project' && s.category === 'active'), 'una etapa de proyecto activa');
    }
    if (target === 'item') {
        return need(stages.find((s) => s.kind_scope === 'task' && s.category === 'inbox' && s.is_capture_default) ?? stages.find((s) => s.kind_scope === 'task' && s.category === 'inbox'), 'Inbox');
    }
    if (target === 'task') {
        // A subtask has no stage of its own and keeps none.
        if (block.parent_id !== null && block.stage_id === null)
            return null;
        if (current?.category === 'active') {
            return need(stages.find((s) => s.kind_scope === 'task' && s.category === 'active'), 'una etapa de tareas activa');
        }
        return need(stages.find((s) => s.kind_scope === 'task' && s.key === 'backlog') ??
            stages.find((s) => s.kind_scope === 'task' && s.category === 'deferred'), 'Backlog');
    }
    return null;
}
export function convertKind(input) {
    const p = convertKindInput.parse(input);
    const { block, target } = p;
    if (target === block.kind)
        return { operations: [], undo: [], withdrawn: 0 };
    if (target === 'file') {
        throw new DomainError('invalid_input', 'Convertir en archivo necesita un adjunto.');
    }
    const now = p.now ?? new Date().toISOString();
    const props = parseProps(block.props);
    let nextProps = props;
    if (target === 'link') {
        const url = p.url?.trim() ?? '';
        if (!isValidLinkUrl(url)) {
            throw new DomainError('invalid_input', 'La dirección no es válida: usa http:// o https://.');
        }
        nextProps = { ...props, url };
    }
    const actionable = isActionableKind(target);
    const stage = actionable ? targetStage(target, block, p.stages) : null;
    const stageId = stage?.id ?? null;
    // A block that was already actionable keeps its status (a done task stays done).
    const status = actionable ? (isActionableKind(block.kind) ? block.status : 'open') : null;
    const completedAt = actionable && status === 'done' ? block.completed_at : null;
    const next = { kind: target };
    const before = { kind: block.kind };
    if (stageId !== block.stage_id) {
        next.stage_id = stageId;
        next.stage_changed_at = now;
        before.stage_id = block.stage_id;
        before.stage_changed_at = block.stage_changed_at;
    }
    if (status !== block.status) {
        next.status = status;
        before.status = block.status;
    }
    if (completedAt !== block.completed_at) {
        next.completed_at = completedAt;
        before.completed_at = block.completed_at;
    }
    if (nextProps !== props) {
        next.props = nextProps;
        before.props = props;
    }
    const withdrawal = withdrawIneligible(p.highlights, { kind: target, category: stage?.category ?? null }, now);
    const id = block.id;
    return {
        operations: [...withdrawal.before, { table: 'blocks', op: 'PATCH', id, data: next }],
        undo: [{ table: 'blocks', op: 'PATCH', id, data: before }, ...withdrawal.after],
        withdrawn: withdrawal.withdrawn,
    };
}
const setLinkUrlInput = z.object({
    block: z.object({ id: uuidSchema, props: z.unknown().optional() }),
    url: z.string(),
});
/** «Cambiar» the address of a link (design D5): only `props.url` changes, the rest of `props` stays. */
export function setLinkUrl(input) {
    const p = setLinkUrlInput.parse(input);
    const url = p.url.trim();
    if (!isValidLinkUrl(url)) {
        throw new DomainError('invalid_input', 'La dirección no es válida: usa http:// o https://.');
    }
    const props = parseProps(p.block.props);
    if (props.url === url)
        return { operations: [], undo: [] };
    const id = p.block.id;
    return {
        operations: [{ table: 'blocks', op: 'PATCH', id, data: { props: { ...props, url } } }],
        undo: [{ table: 'blocks', op: 'PATCH', id, data: { props } }],
    };
}
/** The address of a link as stored in `props`, or null. */
export function linkUrlOf(props) {
    const url = parseProps(props).url;
    return typeof url === 'string' ? url : null;
}
/**
 * Kinds a block can become from the panel or `⌘K` (design D4): never its own nor a file, and an
 * element only from the Inbox (sending a task back there is a move, not a conversion).
 */
export function conversionTargets(kind, stageCategory) {
    const targets = ['task', 'project', 'note', 'page', 'link'];
    return (stageCategory === 'inbox' ? ['item', ...targets] : targets).filter((target) => target !== kind);
}
//# sourceMappingURL=convert.js.map